import { Link } from "react-router-dom";
import { Church, Mail, Phone, MapPin } from "lucide-react";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [subLoading, setSubLoading] = useState(false);

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("Subscribed! Thank you.");
      setEmail("");
    } catch { toast.error("Something went wrong."); }
    finally { setSubLoading(false); }
  };

  return (
    <footer data-testid="footer" className="bg-[#1C2E4A] text-[#FAF9F6] py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Newsletter */}
        <div className="mb-12 pb-12 border-b border-[#FAF9F6]/10">
          <div className="max-w-md mx-auto text-center">
            <h3 className="font-serif text-xl font-semibold mb-2">Stay Connected</h3>
            <p className="text-sm text-[#FAF9F6]/60 mb-4">Subscribe for updates, devotionals, and church news.</p>
            <form onSubmit={handleNewsletter} data-testid="newsletter-form" className="flex gap-2">
              <input
                type="email"
                data-testid="newsletter-email-input"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 px-4 py-2.5 rounded-md bg-[#FAF9F6]/10 border border-[#FAF9F6]/20 text-white placeholder-[#FAF9F6]/40 text-sm focus:outline-none focus:border-[#C8A97E]"
                required
              />
              <button
                type="submit"
                data-testid="newsletter-submit-btn"
                disabled={subLoading}
                className="bg-[#C8A97E] text-white hover:bg-[#B3956A] px-5 py-2.5 rounded-md text-sm font-medium transition-colors disabled:opacity-50"
              >
                {subLoading ? "..." : "Subscribe"}
              </button>
            </form>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2.5 mb-4">
              <Church className="w-6 h-6 text-[#C8A97E]" />
              <span className="font-serif text-lg font-semibold tracking-tight">
                ACS Virtual Church
              </span>
            </div>
            <p className="text-xs leading-relaxed text-[#FAF9F6]/60">
              A church supporting families through faith and education.
            </p>
          </div>

          {/* Church */}
          <div>
            <h4 className="text-sm font-semibold mb-3 text-[#C8A97E]">Church</h4>
            <div className="flex flex-col gap-2">
              {[
                { to: "/about", label: "About" },
                { to: "/beliefs", label: "Beliefs" },
                { to: "/bible", label: "KJV Bible" },
                { to: "/leadership", label: "Leadership" },
                { to: "/prayer-request", label: "Prayer Request" },
                { to: "/donate", label: "Tithes & Donations" },
              ].map((link) => (
                <Link key={link.to} to={link.to} className="text-xs text-[#FAF9F6]/60 hover:text-[#C8A97E] transition-colors">{link.label}</Link>
              ))}
            </div>
          </div>

          {/* School */}
          <div>
            <h4 className="text-sm font-semibold mb-3 text-[#C8A97E]">School</h4>
            <div className="flex flex-col gap-2">
              {[
                { to: "/church-school-program", label: "Church School Program" },
                { to: "/start-a-church-school", label: "Start a School" },
                { to: "/our-schools", label: "Our Schools" },
                { to: "/faq", label: "FAQ" },
                { to: "/terms", label: "Terms & Policies" },
                { to: "/terms#privacy", label: "Privacy Policy" },
              ].map((link) => (
                <Link key={link.to} to={link.to} className="text-xs text-[#FAF9F6]/60 hover:text-[#C8A97E] transition-colors">{link.label}</Link>
              ))}
              <a href="https://alabamachurchschool.com" target="_blank" rel="noopener noreferrer" className="text-xs text-[#FAF9F6]/60 hover:text-[#C8A97E] transition-colors">Alabama Church School</a>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-sm font-semibold mb-3 text-[#C8A97E]">Contact</h4>
            <div className="space-y-2.5">
              <a href="mailto:acsvirtualchurch@gmail.com" className="flex items-center gap-2 text-xs text-[#FAF9F6]/60 hover:text-[#C8A97E] transition-colors">
                <Mail className="w-3.5 h-3.5" /> acsvirtualchurch@gmail.com
              </a>
              <a href="tel:+13345533444" className="flex items-center gap-2 text-xs text-[#FAF9F6]/60 hover:text-[#C8A97E] transition-colors">
                <Phone className="w-3.5 h-3.5" /> (334) 553-3444
              </a>
              <div className="flex items-start gap-2 text-xs text-[#FAF9F6]/60">
                <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <div>
                  <span>833 Ewell Road, Slocomb, AL 36375</span>
                  <p className="text-[10px] text-[#FAF9F6]/30 italic mt-0.5">Mailing address only — we are a virtual church.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-[#FAF9F6]/10 text-center text-xs text-[#FAF9F6]/30">
          &copy; {new Date().getFullYear()} ACS Virtual Church. All rights reserved.
        </div>
      </div>
    </footer>
  );
}
